import type { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router";
import { ChevronLeftIcon } from "../../ui/index.js";
import { useCabinet } from "./context.js";

/**
 * Пометка перехода в истории: откуда пришли (путь внутри кабинета, как у `BackLink`). Её ставит страница-список
 * («Ещё», мастера), когда открывает вложенную страницу.
 */
export interface BackState {
  backTo: string;
}

/** Пришли ли сюда со страницы `to`: тогда «Назад» это шаг назад по истории, а не новый переход. */
export function cameFrom(state: unknown, to: string): boolean {
  return typeof state === "object" && state !== null && (state as Partial<BackState>).backTo === to;
}

/**
 * Куда ведёт «Назад» из шапки. Корневые вкладки (календарь, диалоги, клиенты, «Ещё») идут без кнопки.
 * Мастер первой настройки тоже без шапки: там своя «Назад» внизу, рядом с «Далее».
 * `billingIsTab`: у админа подписка сама в нижней панели.
 * `wide`: на широком экране открытый диалог показан рядом со списком, «Назад» к списку не нужна.
 */
export function headerBackTo(
  pathname: string,
  base: string,
  opts: { isOwner: boolean; ownerKnown: boolean; billingIsTab: boolean; wide?: boolean },
): string | null {
  const prefix = `${base}/`;
  if (!pathname.startsWith(prefix)) return null;
  const rest = pathname.slice(prefix.length).replace(/\/$/, "");
  if (/^settings\/masters\/[^/]+$/.test(rest)) {
    if (!opts.ownerKnown) return null;
    return opts.isOwner ? "settings/masters" : "settings";
  }
  if (rest.startsWith("settings/")) return "settings";
  if (rest.startsWith("conversations/")) return opts.wide ? null : "conversations";
  if (rest.startsWith("clients/")) return "clients";
  if (rest === "demo" || rest === "reports") return "settings";
  if (rest === "billing" && !opts.billingIsTab) return "settings";
  return null;
}

/**
 * «‹ Назад» к разделу «Ещё» (или к указанному пути внутри кабинета). `onClick` может отменить переход (`preventDefault`).
 * Если сюда пришли с этой самой страницы (пометка `BackState` в истории), ссылка делает шаг назад по истории:
 * страница открывается на том же месте прокрутки, как после системной «назад» на Android. Без пометки
 * (прямой заход, ссылка из уведомления, новая вкладка) обычный переход по ссылке.
 */
export function BackLink({
  to = "settings",
  label,
  className,
  onClick,
}: {
  to?: string;
  label?: string;
  className?: string;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
}) {
  const { t } = useTranslation();
  const { base } = useCabinet();
  const { state } = useLocation();
  const navigate = useNavigate();
  const historyBack = cameFrom(state, to);
  return (
    <Link
      to={`${base}/${to}`}
      onClick={(e) => {
        onClick?.(e);
        // Ctrl/Cmd-клик открывает новую вкладку: его не трогаем.
        if (e.defaultPrevented || !historyBack || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate(-1);
      }}
      className={`inline-flex items-center gap-1 ${className ?? "mb-2 py-1 text-sm text-slate-500"}`}
    >
      <ChevronLeftIcon className="-ml-1 h-4 w-4 shrink-0" />
      {label ?? t("common.back")}
    </Link>
  );
}
